import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { EventRecord } from "../lib/types";
import { invokeAdmin, errMsg } from "../lib/adminApi";

// admin_event_get returns the event row spread together with RSVP attendance counts.
type Attendance = { going: number; checked_in: number };
type EventDetailResponse = EventRecord & { attendance?: Attendance };

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventRecord | null>(null);
  const [attendance, setAttendance] = useState<Attendance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rawExpanded, setRawExpanded] = useState(false);

  const [draft, setDraft] = useState({
    name: "",
    description: "",
    start: "",
    end: "",
    url: "",
    place_id: "",
  });

  // Reusable loader: run on mount AND after a successful save (admin_event_update
  // returns {success:true}, not the record, so we must refetch to get fresh state
  // and reset the draft from it).
  const fetchEvent = useCallback(async () => {
    if (!id) return;
    const data = await invokeAdmin<EventDetailResponse>("admin_event_get", { id });
    setEvent(data);
    setAttendance(data.attendance ?? null);
    setDraft({
      name: data.props.name ?? "",
      description: data.props.description ?? "",
      start: data.props.start ?? "",
      end: data.props.end ?? "",
      url: data.props.url ?? "",
      place_id: data.place_id ?? "",
    });
  }, [id]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await fetchEvent();
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, fetchEvent]);

  // All hooks are declared above, so it's safe to bail on a missing route param
  // here (the /records/events/:id route always supplies one).
  if (!id) return null;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await invokeAdmin("admin_event_update", {
        id,
        props_patch: {
          name: draft.name,
          description: draft.description,
          start: draft.start,
          end: draft.end,
          url: draft.url,
        },
      });
      // admin_event_update returns {success:true}, not the record — refetch to
      // get fresh state and reset the draft from it.
      await fetchEvent();
      setEditing(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    if (!event) return;
    setDraft({
      name: event.props.name ?? "",
      description: event.props.description ?? "",
      start: event.props.start ?? "",
      end: event.props.end ?? "",
      url: event.props.url ?? "",
      place_id: event.place_id ?? "",
    });
    setEditing(false);
  }

  async function handleDelete() {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    setDeleting(true);
    setError(null);
    try {
      await invokeAdmin("admin_event_delete", { id });
      navigate("/records");
    } catch (e) {
      setError(errMsg(e));
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div>
        <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
          ← Records
        </Link>
        <p className="mt-4 text-sm text-zinc-500">Loading…</p>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div>
        <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
          ← Records
        </Link>
        <div className="mt-4 rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!event) return null;

  type DraftKey = keyof typeof draft;
  const FIELDS: { key: DraftKey; label: string; multiline?: boolean; readOnly?: boolean }[] = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description", multiline: true },
    { key: "start", label: "Start" },
    { key: "end", label: "End" },
    { key: "url", label: "URL" },
    { key: "place_id", label: "Place ID", readOnly: true },
  ];

  return (
    <div>
      <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
        ← Records
      </Link>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{event.props.name ?? "Event"}</h1>
          <p className="mt-0.5 text-xs text-zinc-500 font-mono">{event.id}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {editing ? (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={cancelEdit}
                className="rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="rounded bg-red-800 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 space-y-4">
        {FIELDS.map(({ key, label, multiline, readOnly }) => (
          <div key={key}>
            <label className="text-xs text-zinc-500 uppercase">{label}</label>
            {editing && !readOnly ? (
              multiline ? (
                <textarea
                  value={draft[key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              ) : (
                <input
                  type="text"
                  value={draft[key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              )
            ) : (
              <p className="mt-1 text-sm text-zinc-300 font-mono break-all">
                {draft[key] || "—"}
              </p>
            )}
          </div>
        ))}

        {event.place_id && (
          <div>
            <Link
              to={`/records/places/${event.place_id}`}
              className="text-xs text-zinc-400 hover:text-zinc-100 underline"
            >
              View place →
            </Link>
          </div>
        )}

        <div>
          <span className="text-xs text-zinc-500 uppercase">Created</span>
          <p className="mt-1 text-sm text-zinc-300">{new Date(event.created).toLocaleString()}</p>
        </div>

        {attendance && (
          <div>
            <span className="text-xs text-zinc-500 uppercase">Attendance</span>
            <p className="mt-1 text-sm text-zinc-300">
              🎟 {attendance.going} going · {attendance.checked_in} checked in
            </p>
          </div>
        )}
      </div>

      <div className="mt-4">
        <button
          onClick={() => setRawExpanded((v) => !v)}
          className="text-xs text-zinc-500 hover:text-zinc-300"
        >
          {rawExpanded ? "Hide" : "Show"} raw props
        </button>
        {rawExpanded && (
          <pre className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-xs text-zinc-300 overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(event.props, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
